import { useState, useEffect, useRef } from 'react';

const PomodoroTimer = ({ darkMode = false }) => {
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [sessions, setSessions] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Load sessions from localStorage
    const savedSessions = localStorage.getItem('focusSessions');
    if (savedSessions) {
      try {
        const parsedSessions = JSON.parse(savedSessions);
        setSessions(parsedSessions.length || 0);
      } catch (error) {
        console.error('Error loading focus sessions:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (isActive && !isPaused && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(timeLeft => timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleSessionComplete();
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isActive, isPaused, timeLeft]);

  const handleSessionComplete = () => {
    setIsActive(false);
    setIsPaused(false);
    
    // Save completed session
    const newSession = {
      duration: 25,
      completedAt: new Date().toISOString(),
      type: 'pomodoro'
    };
    
    const existingSessions = JSON.parse(localStorage.getItem('focusSessions') || '[]');
    const updatedSessions = [newSession, ...existingSessions];
    localStorage.setItem('focusSessions', JSON.stringify(updatedSessions));
    
    setSessions(prev => prev + 1);
    setTimeLeft(25 * 60);
    
    // Show notification or play sound here
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Pomodoro Complete! 🍅', {
        body: 'Great job! Time for a break.',
        icon: '/favicon.ico'
      });
    }
  };

  const handleStart = () => {
    setIsActive(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(!isPaused);
  };

  const handleReset = () => {
    setIsActive(false);
    setIsPaused(false);
    setTimeLeft(25 * 60);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = ((25 * 60 - timeLeft) / (25 * 60)) * 100;

  return (
    <div className="text-center space-y-8">
      <div className="space-y-2">
        <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
          🍅 Pomodoro Timer
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          Focus for 25 minutes, then take a break
        </p>
      </div>

      {/* Timer Display */}
      <div className="relative">
        <div className="w-64 h-64 mx-auto relative">
          {/* Progress Ring */}
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              className="text-gray-200 dark:text-gray-700"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
              strokeDasharray={283}
              strokeDashoffset={283 - (283 * progress) / 100}
              className="text-purple-500 transition-all duration-1000 ease-out"
              strokeLinecap="round"
            />
          </svg>
          
          {/* Timer Text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-mono font-bold text-gray-900 dark:text-white">
                {formatTime(timeLeft)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {isActive && !isPaused ? 'Focus Time' : isPaused ? 'Paused' : 'Ready to Focus'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Control Buttons */}
      <div className="flex justify-center space-x-4">
        {!isActive ? (
          <button
            onClick={handleStart}
            className="wellness-button bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600 shadow-lg"
          >
            Start Focus
          </button>
        ) : (
          <button
            onClick={handlePause}
            className="wellness-button bg-gradient-to-r from-yellow-500 to-orange-500 text-white hover:from-yellow-600 hover:to-orange-600 shadow-lg"
          >
            {isPaused ? 'Resume' : 'Pause'}
          </button>
        )}
        
        <button
          onClick={handleReset}
          className="wellness-button bg-gradient-to-r from-gray-500 to-gray-600 text-white hover:from-gray-600 hover:to-gray-700 shadow-lg"
        >
          Reset
        </button>
      </div>

      {/* Stats */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Today's Progress
        </h3>
        <div className="flex justify-center items-center space-x-8">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {sessions}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Sessions
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-pink-600 dark:text-pink-400">
              {Math.round(sessions * 25)}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300">
              Minutes
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PomodoroTimer;
